import React, { useEffect, useRef, useState } from "react";
import { Button, Typography, Divider } from "antd";

const { Text, Title } = Typography;

const CANVAS_W = 900;
const CANVAS_H = 700;
const CAM_W = 240;
const CAM_H = 180;
const MAX_MISSED = 10;
const SMOOTHING_FACTOR = 0.3;

export default function BalloonPopGame() {
  const videoRef = useRef(null);
  const camCanvasRef = useRef(null);
  const gameCanvasRef = useRef(null);
  const rafRef = useRef(null);
  const streamRef = useRef(null);
  const handModelRef = useRef(null);
  const runningRef = useRef(false);
  const gameStartedRef = useRef(false);

  const [running, setRunning] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);
  const [score, setScore] = useState(0);
  const [popped, setPopped] = useState(0);
  const [missed, setMissed] = useState(0);
  const [combo, setCombo] = useState(0);
  const [status, setStatus] = useState("Click Start to begin");
  const [gameOver, setGameOver] = useState(false);

  const gameRef = useRef({
    leftPin: null,
    rightPin: null,
    smoothLeftPin: null,
    smoothRightPin: null,
    balloons: [],
    particles: [],
    nextBalloonTime: Date.now(),
    speedMultiplier: 1,
    comboTimer: 0,
  });

  const balloonTypes = [
    { color: "#FF6B6B", hits: 1, points: 10, spawnChance: 0.5 },
    { color: "#4ECDC4", hits: 2, points: 25, spawnChance: 0.35 },
    { color: "#FFD93D", hits: 3, points: 50, spawnChance: 0.15 },
  ];

  useEffect(() => {
    drawInitialState();

    // Initialize camera preview canvas
    if (camCanvasRef.current) {
      const ctx = camCanvasRef.current.getContext("2d");
      ctx.fillStyle = "#000";
      ctx.fillRect(0, 0, CAM_W, CAM_H);
      ctx.fillStyle = "#666";
      ctx.font = "14px Arial";
      ctx.textAlign = "center";
      ctx.fillText("Press Start to", CAM_W / 2, CAM_H / 2 - 10);
      ctx.fillText("activate camera", CAM_W / 2, CAM_H / 2 + 10);
    }

    return () => stopGame();
  }, []);

  function drawInitialState() {
    if (!gameCanvasRef.current) return;
    const ctx = gameCanvasRef.current.getContext("2d");
    const gradient = ctx.createLinearGradient(0, 0, CANVAS_W, CANVAS_H);
    gradient.addColorStop(0, "#667eea");
    gradient.addColorStop(1, "#764ba2");
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);

    if (!gameStartedRef.current && !gameOver) {
      ctx.fillStyle = "#fff";
      ctx.font = "bold 32px Arial";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("Click Start Game to begin", CANVAS_W / 2, CANVAS_H / 2);
    }
  }

  async function loadHandTrackingModel() {
    try {
      setStatus("Loading hand tracking model...");

      // Dynamic import of MediaPipe HandLandmarker
      const visionModule = await import(
        "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest"
      );

      const HandLandmarker = visionModule.HandLandmarker;
      const FilesetResolver = visionModule.FilesetResolver;

      const vision = await FilesetResolver.forVisionTasks(
        "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.0/wasm"
      );

      handModelRef.current = await HandLandmarker.createFromOptions(vision, {
        baseOptions: {
          modelAssetPath:
            "https://storage.googleapis.com/mediapipe-models/hand_landmarker/hand_landmarker/float16/1/hand_landmarker.task",
        },
        runningMode: "VIDEO",
        numHands: 2,
      });

      setStatus("📍 Ready! Press Start Game to begin!");
      return true;
    } catch (e) {
      console.error("Failed to load hand tracking model:", e);
      setStatus(`Failed to load model: ${e.message}`);
      return false;
    }
  }

  function updateHands() {
    if (
      !handModelRef.current ||
      !videoRef.current ||
      videoRef.current.readyState < 2
    ) {
      return;
    }

    try {
      const results = handModelRef.current.detectForVideo(
        videoRef.current,
        performance.now()
      );

      const g = gameRef.current;
      g.leftPin = null;
      g.rightPin = null;

      if (results.landmarks && results.landmarks.length > 0) {
        for (let i = 0; i < results.landmarks.length; i++) {
          const landmarks = results.landmarks[i];
          const handedness = results.handednesses[i][0].categoryName;

          const indexTip = landmarks[8];
          const pinX = (1 - indexTip.x) * CANVAS_W;
          const pinY = indexTip.y * CANVAS_H;

          const pin = { x: pinX, y: pinY };

          if (handedness === "Left") {
            g.leftPin = pin;
          } else {
            g.rightPin = pin;
          }
        }
      }

      // Apply smoothing
      if (g.leftPin) {
        if (!g.smoothLeftPin) {
          g.smoothLeftPin = { ...g.leftPin };
        } else {
          g.smoothLeftPin.x +=
            (g.leftPin.x - g.smoothLeftPin.x) * SMOOTHING_FACTOR;
          g.smoothLeftPin.y +=
            (g.leftPin.y - g.smoothLeftPin.y) * SMOOTHING_FACTOR;
        }
      } else {
        g.smoothLeftPin = null;
      }

      if (g.rightPin) {
        if (!g.smoothRightPin) {
          g.smoothRightPin = { ...g.rightPin };
        } else {
          g.smoothRightPin.x +=
            (g.rightPin.x - g.smoothRightPin.x) * SMOOTHING_FACTOR;
          g.smoothRightPin.y +=
            (g.rightPin.y - g.smoothRightPin.y) * SMOOTHING_FACTOR;
        }
      } else {
        g.smoothRightPin = null;
      }
    } catch (e) {
      console.error("Hand detection error:", e);
    }
  }

  function spawnBalloon() {
    let random = Math.random();
    let cumulativeChance = 0;
    let type = balloonTypes[0];

    for (let t of balloonTypes) {
      cumulativeChance += t.spawnChance;
      if (random <= cumulativeChance) {
        type = t;
        break;
      }
    }

    const edge = Math.floor(Math.random() * 4);
    let x, y, vx, vy;
    const g = gameRef.current;
    const baseSpeed = 2.5 + Math.random() * 2;
    const speed = baseSpeed * g.speedMultiplier;

    switch (edge) {
      case 0: // Top
        x = Math.random() * CANVAS_W;
        y = -50;
        vx = (Math.random() - 0.5) * 2 * g.speedMultiplier;
        vy = speed;
        break;
      case 1: // Right
        x = CANVAS_W + 50;
        y = Math.random() * CANVAS_H;
        vx = -speed;
        vy = (Math.random() - 0.5) * 2 * g.speedMultiplier;
        break;
      case 2: // Bottom
        x = Math.random() * CANVAS_W;
        y = CANVAS_H + 50;
        vx = (Math.random() - 0.5) * 2 * g.speedMultiplier;
        vy = -speed;
        break;
      case 3: // Left
        x = -50;
        y = Math.random() * CANVAS_H;
        vx = speed;
        vy = (Math.random() - 0.5) * 2 * g.speedMultiplier;
        break;
    }

    const balloon = {
      x,
      y,
      vx,
      vy,
      size: 35,
      color: type.color,
      maxHits: type.hits,
      currentHits: 0,
      points: type.points,
      wobble: Math.random() * Math.PI * 2,
      wobbleSpeed: 0.03 + Math.random() * 0.03,
    };

    g.balloons.push(balloon);
    console.log(
      `Balloon spawned at edge ${edge}, total balloons: ${g.balloons.length}`,
      balloon
    );
  }

  function checkPinCollision(balloon, pin) {
    if (!pin) return false;

    const dx = balloon.x - pin.x;
    const dy = balloon.y - pin.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    if (distance < balloon.size) {
      balloon.currentHits++;
      createHitEffect(balloon.x, balloon.y, balloon.color);

      if (balloon.currentHits >= balloon.maxHits) {
        return true; // Balloon popped
      } else {
        balloon.size *= 0.85;
      }
    }
    return false;
  }

  function createHitEffect(x, y, color) {
    const g = gameRef.current;
    for (let i = 0; i < 5; i++) {
      const angle = (Math.PI * 2 * i) / 5;
      g.particles.push({
        x,
        y,
        vx: Math.cos(angle) * 2,
        vy: Math.sin(angle) * 2,
        size: 4,
        color,
        life: 1,
      });
    }
  }

  function createPopEffect(x, y, color) {
    const g = gameRef.current;
    for (let i = 0; i < 20; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = 2 + Math.random() * 5;
      g.particles.push({
        x,
        y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        size: 3 + Math.random() * 6,
        color,
        life: 1,
      });
    }
  }

  function updateGame() {
    const g = gameRef.current;

    // Spawn balloons
    if (Date.now() > g.nextBalloonTime) {
      spawnBalloon();
      g.nextBalloonTime = Date.now() + (800 + Math.random() * 1200);
    }

    // Update balloons
    for (let i = g.balloons.length - 1; i >= 0; i--) {
      const balloon = g.balloons[i];

      balloon.x += balloon.vx;
      balloon.y += balloon.vy;
      balloon.wobble += balloon.wobbleSpeed;

      // Check collisions
      const leftHit = checkPinCollision(balloon, g.smoothLeftPin);
      const rightHit = checkPinCollision(balloon, g.smoothRightPin);

      if (leftHit || rightHit) {
        // Balloon popped
        setScore((s) => s + balloon.points);
        setPopped((p) => {
          const newPopped = p + 1;
          g.speedMultiplier = 1 + Math.floor(newPopped / 5) * 0.2;
          return newPopped;
        });
        setCombo((c) => c + 1);
        g.comboTimer = 120;
        createPopEffect(balloon.x, balloon.y, balloon.color);
        g.balloons.splice(i, 1);
        continue;
      }

      // Check if escaped
      if (
        balloon.x < -100 ||
        balloon.x > CANVAS_W + 100 ||
        balloon.y < -100 ||
        balloon.y > CANVAS_H + 100
      ) {
        g.balloons.splice(i, 1);
        setMissed((m) => {
          const newMissed = m + 1;
          if (newMissed >= MAX_MISSED) {
            endGame();
          }
          return newMissed;
        });
        setCombo(0);
        g.comboTimer = 0;
      }
    }

    // Update particles
    for (let i = g.particles.length - 1; i >= 0; i--) {
      const p = g.particles[i];
      p.x += p.vx;
      p.y += p.vy;
      p.vy += 0.15;
      p.life -= 0.015;

      if (p.life <= 0) {
        g.particles.splice(i, 1);
      }
    }

    // Update combo timer
    if (g.comboTimer > 0) {
      g.comboTimer--;
      if (g.comboTimer === 0) {
        setCombo(0);
      }
    }
  }

  function drawBalloon(ctx, balloon) {
    const wobbleX = Math.sin(balloon.wobble) * 4;

    ctx.save();

    // Balloon string
    ctx.strokeStyle = "rgba(255, 255, 255, 0.3)";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(balloon.x + wobbleX, balloon.y + balloon.size);
    ctx.lineTo(balloon.x + wobbleX, balloon.y + balloon.size + 20);
    ctx.stroke();

    // Balloon shadow
    ctx.fillStyle = "rgba(0, 0, 0, 0.2)";
    ctx.beginPath();
    ctx.arc(
      balloon.x + wobbleX + 2,
      balloon.y + 2,
      balloon.size,
      0,
      Math.PI * 2
    );
    ctx.fill();

    // Balloon body
    ctx.fillStyle = balloon.color;
    ctx.beginPath();
    ctx.arc(balloon.x + wobbleX, balloon.y, balloon.size, 0, Math.PI * 2);
    ctx.fill();

    // Balloon highlight
    ctx.fillStyle = "rgba(255, 255, 255, 0.4)";
    ctx.beginPath();
    ctx.arc(
      balloon.x + wobbleX - balloon.size * 0.3,
      balloon.y - balloon.size * 0.3,
      balloon.size * 0.3,
      0,
      Math.PI * 2
    );
    ctx.fill();

    // Hit counter
    if (balloon.currentHits > 0) {
      ctx.fillStyle = "white";
      ctx.font = "bold 18px Arial";
      ctx.textAlign = "center";
      ctx.fillText(
        `${balloon.currentHits}/${balloon.maxHits}`,
        balloon.x + wobbleX,
        balloon.y + 5
      );
    }

    ctx.restore();
  }

  function drawPin(ctx, pin, color) {
    if (!pin) return;

    // Pin needle
    ctx.fillStyle = "#C0C0C0";
    ctx.beginPath();
    ctx.moveTo(pin.x, pin.y - 15);
    ctx.lineTo(pin.x - 3, pin.y + 5);
    ctx.lineTo(pin.x + 3, pin.y + 5);
    ctx.closePath();
    ctx.fill();

    // Pin head
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(pin.x, pin.y + 8, 8, 0, Math.PI * 2);
    ctx.fill();

    // Pin head highlight
    ctx.fillStyle = "rgba(255, 255, 255, 0.5)";
    ctx.beginPath();
    ctx.arc(pin.x - 2, pin.y + 6, 3, 0, Math.PI * 2);
    ctx.fill();

    // Pin glow
    const gradient = ctx.createRadialGradient(
      pin.x,
      pin.y,
      0,
      pin.x,
      pin.y,
      20
    );
    gradient.addColorStop(0, color + "40");
    gradient.addColorStop(1, color + "00");
    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(pin.x, pin.y, 20, 0, Math.PI * 2);
    ctx.fill();
  }

  function drawParticle(ctx, p) {
    ctx.fillStyle = p.color;
    ctx.globalAlpha = p.life;
    ctx.beginPath();
    ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalAlpha = 1;
  }

  function draw() {
    const ctx = gameCanvasRef.current?.getContext("2d");
    if (!ctx) return;

    // Background gradient
    const gradient = ctx.createLinearGradient(0, 0, CANVAS_W, CANVAS_H);
    gradient.addColorStop(0, "#667eea");
    gradient.addColorStop(1, "#764ba2");
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);

    const g = gameRef.current;

    // Debug: Log what we're about to draw
    if (g.balloons.length > 0 && Math.random() < 0.1) {
      console.log(
        `Drawing ${g.balloons.length} balloons, ${g.particles.length} particles`
      );
      console.log("First balloon:", g.balloons[0]);
    }

    // Draw particles
    g.particles.forEach((p) => drawParticle(ctx, p));

    // Draw balloons
    g.balloons.forEach((b) => drawBalloon(ctx, b));

    // Draw pins
    drawPin(ctx, g.smoothLeftPin, "#FF4444");
    drawPin(ctx, g.smoothRightPin, "#44FF44");

    // Game over overlay
    if (gameOver) {
      console.log("Drawing game over screen");
      ctx.fillStyle = "rgba(0, 0, 0, 0.85)";
      ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);

      ctx.fillStyle = "#FF4444";
      ctx.font = "bold 64px Arial";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("🎈 GAME OVER! 🎈", CANVAS_W / 2, CANVAS_H / 2 - 80);

      ctx.fillStyle = "#fff";
      ctx.font = "bold 36px Arial";
      ctx.fillText(`Final Score: ${score}`, CANVAS_W / 2, CANVAS_H / 2 - 10);

      ctx.font = "24px Arial";
      ctx.fillText(
        "Too many balloons escaped!",
        CANVAS_W / 2,
        CANVAS_H / 2 + 40
      );

      ctx.font = "bold 28px Arial";
      ctx.fillStyle = "#4ade80";
      ctx.fillText(
        "Click Start to play again",
        CANVAS_W / 2,
        CANVAS_H / 2 + 100
      );
    } else if (!runningRef.current && !gameStartedRef.current) {
      // Initial screen when game hasn't started (check refs not state)
      ctx.fillStyle = "#fff";
      ctx.font = "bold 32px Arial";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("Click Start Game to begin", CANVAS_W / 2, CANVAS_H / 2);
    }
  }

  function drawCameraPreview() {
    const canvas = camCanvasRef.current;
    const video = videoRef.current;

    if (!canvas || !video) {
      return;
    }

    if (video.readyState < 2) {
      // Video not ready yet, draw black screen
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#000";
      ctx.fillRect(0, 0, CAM_W, CAM_H);
      ctx.fillStyle = "#666";
      ctx.font = "12px Arial";
      ctx.textAlign = "center";
      ctx.fillText("Waiting for camera...", CAM_W / 2, CAM_H / 2);
      return;
    }

    try {
      const ctx = canvas.getContext("2d");

      // Draw video frame (mirrored)
      ctx.save();
      ctx.scale(-1, 1);
      ctx.drawImage(video, -CAM_W, 0, CAM_W, CAM_H);
      ctx.restore();

      // Draw labels only
      ctx.fillStyle = "#fff";
      ctx.font = "bold 14px Arial";
      ctx.strokeStyle = "#000";
      ctx.lineWidth = 3;

      ctx.strokeText("LEFT", 15, 25);
      ctx.fillText("LEFT", 15, 25);

      ctx.strokeText("RIGHT", CAM_W - 55, 25);
      ctx.fillText("RIGHT", CAM_W - 55, 25);
    } catch (e) {
      console.error("Error drawing camera preview:", e);
    }
  }

  function gameLoop() {
    // Always update hands and draw camera preview if we have video
    if (videoRef.current && videoRef.current.readyState >= 2) {
      updateHands();
      drawCameraPreview();
    }

    // Only update game logic if actually playing (use refs for immediate access)
    if (runningRef.current && gameStartedRef.current && !gameOver) {
      updateGame();

      // Debug: Log balloon count periodically
      if (Math.random() < 0.01) {
        console.log(
          `Game running: balloons=${
            gameRef.current.balloons.length
          }, nextSpawn=${gameRef.current.nextBalloonTime - Date.now()}`
        );
      }
    }

    // Always draw the game canvas (including game over screen)
    draw();

    // Keep the loop running always (so we can see game over screen)
    rafRef.current = requestAnimationFrame(gameLoop);
  }

  function endGame() {
    console.log("=== GAME OVER ===");
    setGameOver(true);
    runningRef.current = false;
    gameStartedRef.current = false;
    setGameStarted(false);
    setRunning(false);
    setStatus("💥 Game Over! Too many balloons escaped!");
    console.log("Game over state set to true");
  }

  function resetGameState() {
    setScore(0);
    setPopped(0);
    setMissed(0);
    setCombo(0);
    setGameOver(false);
    runningRef.current = false;
    gameStartedRef.current = false;
    setGameStarted(false);
    setRunning(false);

    gameRef.current = {
      leftPin: null,
      rightPin: null,
      smoothLeftPin: null,
      smoothRightPin: null,
      balloons: [],
      particles: [],
      nextBalloonTime: Date.now(), // Initialize to now so balloons spawn immediately
      speedMultiplier: 1,
      comboTimer: 0,
    };

    if (handModelRef.current) {
      setStatus("📍 Ready! Press Start Game to begin!");
    } else {
      setStatus("Click Start to begin");
    }

    drawInitialState();
  }

  async function startGame() {
    if (running) return;

    console.log("=== Starting Game ===");
    setStatus("Loading hand tracking model...");

    if (!handModelRef.current) {
      const loaded = await loadHandTrackingModel();
      if (!loaded) return;
    }

    resetGameState();
    setStatus("Requesting camera access...");

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 640 },
          height: { ideal: 480 },
          facingMode: "user",
        },
      });

      streamRef.current = stream;

      if (!videoRef.current) {
        videoRef.current = document.createElement("video");
      }

      videoRef.current.playsInline = true;
      videoRef.current.muted = true;
      videoRef.current.autoplay = true;
      videoRef.current.srcObject = stream;

      await new Promise((resolve) => {
        let resolved = false;
        const checkReady = () => {
          if (resolved) return;
          if (videoRef.current.readyState >= 2) {
            resolved = true;
            resolve();
          }
        };

        const events = ["loadedmetadata", "loadeddata", "canplay"];
        events.forEach((event) =>
          videoRef.current.addEventListener(event, checkReady)
        );

        const interval = setInterval(checkReady, 100);

        setTimeout(() => {
          clearInterval(interval);
          events.forEach((event) =>
            videoRef.current.removeEventListener(event, checkReady)
          );
          if (!resolved) {
            resolved = true;
            resolve();
          }
        }, 5000);
      });

      try {
        await videoRef.current.play();
      } catch (e) {
        console.log("Play error (might be ok):", e);
      }

      await new Promise((resolve) => setTimeout(resolve, 500));

      runningRef.current = true;
      gameStartedRef.current = true;
      setRunning(true);
      setGameStarted(true);
      console.log("Game states set: running=true, gameStarted=true");
      console.log("Refs set: runningRef=true, gameStartedRef=true");
      console.log(
        "Initial balloon spawn time:",
        gameRef.current.nextBalloonTime
      );
      console.log("Current time:", Date.now());

      setStatus("🎈 Playing! Move your hands to pop balloons!");

      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }

      // Start the continuous game loop
      rafRef.current = requestAnimationFrame(gameLoop);
      console.log("Game loop started");
    } catch (e) {
      console.error("Camera startup error:", e);
      setStatus(`Camera error: ${e.message}`);
    }
  }

  function stopGame() {
    runningRef.current = false;
    gameStartedRef.current = false;
    setRunning(false);
    setGameStarted(false);

    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    setStatus("Stopped – Click Start to play again");
    drawInitialState();
  }

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: "#2a5298" }}>
      <div
        style={{
          width: 320,
          background: "#1f1f1f",
          padding: 16,
          color: "#fff",
        }}
      >
        <Title level={3} style={{ color: "#fff", marginBottom: 16 }}>
          📍 Balloon Pop Challenge
        </Title>

        <Text
          style={{
            color: "#e6eef8",
            marginBottom: 8,
            fontSize: 13,
            fontWeight: "bold",
            display: "block",
          }}
        >
          {status}
        </Text>

        <Divider
          style={{ margin: "16px 0", borderColor: "rgba(255,255,255,0.2)" }}
        />

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            marginBottom: 16,
          }}
        >
          <Button
            onClick={startGame}
            disabled={running}
            type="primary"
            block
            size="large"
            style={{
              fontWeight: "bold",
              background: running ? "#555" : "#1890ff",
              borderColor: running ? "#555" : "#1890ff",
            }}
          >
            Start Game
          </Button>
          <Button
            onClick={stopGame}
            disabled={!running}
            danger
            block
            size="large"
            style={{
              background: !running ? "#555" : "#ff4d4f",
              borderColor: !running ? "#555" : "#ff4d4f",
              color: "#fff",
            }}
          >
            Stop Game
          </Button>
          <Button
            onClick={resetGameState}
            type="primary"
            block
            ghost
            size="large"
          >
            Reset
          </Button>
        </div>

        <div style={{ marginBottom: 16 }}>
          <Text
            style={{
              color: "#4ade80",
              fontSize: 22,
              fontWeight: "bold",
              display: "block",
            }}
          >
            ⭐ Score: {score}
          </Text>
          <Text
            style={{
              color: "#60a5fa",
              fontSize: 22,
              fontWeight: "bold",
              display: "block",
            }}
          >
            🎈 Popped: {popped}
          </Text>
          <Text
            style={{
              color: "#fbbf24",
              fontSize: 22,
              fontWeight: "bold",
              display: "block",
            }}
          >
            🎯 Combo: {combo}
          </Text>
          <Text
            style={{
              color: "#f87171",
              fontSize: 22,
              fontWeight: "bold",
              display: "block",
            }}
          >
            ❌ Missed: {missed}/{MAX_MISSED}
          </Text>
        </div>

        <Divider
          style={{ margin: "16px 0", borderColor: "rgba(255,255,255,0.2)" }}
        />

        <div
          style={{
            color: "#bfe7ff",
            fontSize: 12,
            marginBottom: 16,
            lineHeight: 1.6,
          }}
        >
          <Title level={5} style={{ color: "#bfe7ff", marginTop: 0 }}>
            🎯 Balloon Types:
          </Title>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 8,
            }}
          >
            <div
              style={{
                width: 20,
                height: 20,
                borderRadius: "50%",
                background: "#FF6B6B",
              }}
            />
            <Text style={{ color: "#bfe7ff" }}>Red: 1 hit = 10pts</Text>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 8,
            }}
          >
            <div
              style={{
                width: 20,
                height: 20,
                borderRadius: "50%",
                background: "#4ECDC4",
              }}
            />
            <Text style={{ color: "#bfe7ff" }}>Cyan: 2 hits = 25pts</Text>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 8,
            }}
          >
            <div
              style={{
                width: 20,
                height: 20,
                borderRadius: "50%",
                background: "#FFD93D",
              }}
            />
            <Text style={{ color: "#bfe7ff" }}>Yellow: 3 hits = 50pts</Text>
          </div>
          <Divider
            style={{ margin: "12px 0", borderColor: "rgba(255,255,255,0.2)" }}
          />
          <Text style={{ color: "#bfe7ff", display: "block", marginBottom: 4 }}>
            • Move your hands to control the pins
          </Text>
          <Text style={{ color: "#bfe7ff", display: "block", marginBottom: 4 }}>
            • Pop balloons before they escape!
          </Text>
          <Text style={{ color: "#bfe7ff", display: "block" }}>
            • Speed increases every 5 balloons popped
          </Text>
        </div>

        <Text
          style={{
            marginBottom: 8,
            color: "#999",
            fontSize: 11,
            fontWeight: "bold",
            display: "block",
          }}
        >
          Camera Preview:
        </Text>
        <canvas
          ref={camCanvasRef}
          width={CAM_W}
          height={CAM_H}
          style={{
            borderRadius: 8,
            width: "100%",
            background: "#000",
            border: "2px solid #333",
          }}
        />
      </div>

      <div
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <canvas
          ref={gameCanvasRef}
          width={CANVAS_W}
          height={CANVAS_H}
          style={{ borderRadius: 8, boxShadow: "0 8px 30px rgba(0,0,0,0.6)" }}
        />
      </div>
    </div>
  );
}
